<template>
  <div v-if="isSupported" class="invoice-settings">
    <button
      type="button"
      class="invoice-settings__toggle"
      :aria-expanded="open ? 'true' : 'false'"
      aria-controls="gateway-invoice-settings"
      @click.prevent="open = !open"
    >
      {{ $t("gateways.invoice_settings", "Invoice settings") }}
      <span class="invoice-settings__caret">{{ open ? "&#9650;" : "&#9660;" }}</span>
    </button>

    <b-collapse id="gateway-invoice-settings" v-model="open">
      <div class="invoice-settings__body">
        <div class="form-group">
          <label for="invoice-settings-service">
            {{ $t("gateways.invoice_settings_service", "Receipt service") }}
          </label>
          <select id="invoice-settings-service" v-model="form.service" class="form-control">
            <option value="">
              {{
                $t(
                  "gateways.invoice_settings_service_none",
                  "None - the gateway issues the receipt"
                )
              }}
            </option>
            <option value="green_invoice">
              {{ $t("gateways.invoice_settings_service_green_invoice", "Green invoice (morning)") }}
            </option>
          </select>
        </div>

        <template v-if="form.service">
          <div class="form-group">
            <label for="invoice-settings-client-id">
              {{ $t("gateways.invoice_settings_client_id", "API key client ID") }}
            </label>
            <input
              id="invoice-settings-client-id"
              v-model="form.client_id"
              type="text"
              class="form-control"
            />
          </div>

          <div class="form-group">
            <label for="invoice-settings-client-secret">
              {{ $t("gateways.invoice_settings_client_secret", "API key client secret") }}
            </label>
            <input
              id="invoice-settings-client-secret"
              v-model="form.client_secret"
              type="password"
              autocomplete="new-password"
              class="form-control"
              :placeholder="secretPlaceholder"
            />
            <small class="text-muted">
              {{
                $t(
                  "gateways.invoice_settings_client_secret_help",
                  "The secret is never displayed back, leave it empty to keep the saved one."
                )
              }}
            </small>
          </div>

          <div class="form-group">
            <label for="invoice-settings-document-type">
              {{ $t("gateways.invoice_settings_document_type", "Document type") }}
            </label>
            <select
              id="invoice-settings-document-type"
              v-model.number="form.document_type"
              class="form-control"
            >
              <option :value="405">
                {{ $t("gateways.invoice_settings_document_type_405", "405 - Donation receipt") }}
              </option>
              <option :value="400">
                {{ $t("gateways.invoice_settings_document_type_400", "400 - Receipt") }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="invoice-settings-lang">
              {{ $t("gateways.invoice_settings_lang", "Document language") }}
            </label>
            <select id="invoice-settings-lang" v-model="form.lang" class="form-control">
              <option value="he">
                {{ $t("gateways.invoice_settings_lang_he", "Hebrew") }}
              </option>
              <option value="en">
                {{ $t("gateways.invoice_settings_lang_en", "English") }}
              </option>
            </select>
          </div>
        </template>

        <input
          type="submit"
          class="bttn bttn--orange bttn--sm"
          :value="$t('account.save')"
          :disabled="saving"
          @click="save"
        />
      </div>
    </b-collapse>
  </div>
</template>

<script>
import axios from "axios";
import urls from "@/constants/urls.js";
import { notificationsMixin } from "@/mixins";

// the receipt service is only supported by the meshulam v2 based gateways
const SUPPORTED_GATEWAYS = [
  "meshulam-v2",
  "meshulam-v2-bit",
  "meshulam-google-pay",
  "meshulam-bank-transfer",
  "meshulam-wallet",
];

export default {
  props: {
    gatewayId: {
      type: [String, Number],
      required: true,
    },
    gatewayInfo: {
      type: Object,
      default: null,
    },
  },

  mixins: [notificationsMixin],

  data() {
    return {
      open: false,
      saving: false,
      form: {
        service: "",
        client_id: "",
        client_secret: "",
        document_type: 405,
        lang: "he",
      },
    };
  },

  computed: {
    isSupported() {
      return SUPPORTED_GATEWAYS.includes(this.gatewayInfo?.attributes?.gateway);
    },

    secretPlaceholder() {
      if (!this.gatewayInfo?.attributes?.gateway_data?.receipt_service) {
        return "";
      }

      return this.$t("gateways.invoice_settings_client_secret_saved", "Saved");
    },
  },

  watch: {
    gatewayInfo: {
      immediate: true,
      handler() {
        this.setForm();
      },
    },
  },

  methods: {
    setForm() {
      const data = this.gatewayInfo?.attributes?.gateway_data || {};

      this.form = {
        service: data.receipt_service || "",
        client_id: data.receipt_service_client_id || "",
        // the secret is never returned by the api, an empty value keeps the
        // saved one
        client_secret: "",
        document_type: Number(data.receipt_service_document_type) || 405,
        lang: data.receipt_service_lang || "he",
      };
    },

    save() {
      let orgId = localStorage.getItem("orgId");

      this.saving = true;

      axios
        .patch(urls.getGatewayData.replace(":orgId", orgId).replace(":gatewayId", this.gatewayId), {
          receipt_service: { ...this.form },
        })
        .then(() => {
          this.$_notificationsMixin_makeToast(
            this.$t("gateways.invoice_settings", "Invoice settings"),
            this.$t("gateways.invoice_settings_saved", "Saved"),
            "success"
          );
          this.$emit("saved");
        })
        .catch(this.$_notificationsMixin_handleCatch)
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.invoice-settings {
  margin-bottom: 20px;
  border-bottom: 1px solid lightgrey;

  &__toggle {
    // .data-view-wrapper floats its buttons right, this one is a full width row
    float: none;
    width: 100%;
    padding: 10px 0;
    background: none;
    border: none;
    text-align: left;
    font-family: $vito-bold;
    font-size: 16px;
    cursor: pointer;
  }

  &__caret {
    font-size: 12px;
    margin-left: 5px;
  }

  &__body {
    padding: 0 0 20px;
  }
}
</style>
